<script context="module">
  import { THEME_NAMES } from "../utilities/themes";
  export const VALID_VARIANTS = /** @type {const} */ (["outline", "filled"]);
  export const VALID_THEMES = THEME_NAMES;
  export const VALID_ICON_ALIGNMENTS = ["leading", "trailing"];
  export const VALID_TYPES = /** @type {const} */ (["button", "submit"]);
</script>

<script>
  // @ts-check
  import LoadingIndicator from "./LoadingIndicator.svelte";
  import { validatePropValue } from "../utilities/logs";
  import { getTheme } from "../utilities/themes";
  import Icon, { ICON_NAMES } from "./Icon.svelte";

  /** Primarily used for testing
   * @type {string | undefined} */
  export let id = undefined;

  /** @type {HTMLElement | undefined} */
  export let ref = undefined;

  /**
   * The external class for the button element.
   * @type {string}
   */
  export let buttonClass = "";

  /**
   * The value to override the componentColorTheme value for the button.
   * @type {string}
   */
  export let componentColorThemeOverride = "";

  /**
   * The type of variants for the button.
   * @type {typeof VALID_VARIANTS[number]}
   */
  export let variant = "outline";
  validatePropValue({
    componentName: "Button",
    propName: "variant",
    propValue: variant,
    validPropValues: [...VALID_VARIANTS]
  });

  /**
   * The theme of the button used to determine if it is black or white.
   * @type {import('../utilities/themes').ThemeName}
   */
  export let theme = "white";

  validatePropValue({
    componentName: "Button",
    propName: "theme",
    propValue: theme,
    validPropValues: VALID_THEMES
  });

  /**
   * The type of the button used to determine if it is submit button or not.
   * @type {typeof VALID_TYPES[number]}
   */
  export let type = "button";
  validatePropValue({
    componentName: "Button",
    propName: "type",
    propValue: type,
    validPropValues: [...VALID_TYPES]
  });

  /** @type { boolean } */
  export let disabled = false;

  /**
   * The url of the button if it behaves as link.
   * @type {string}
   */
  export let url = undefined;

  /**
   * The label of the button.
   * @type {string}
   */
  export let label = undefined;

  /**
   * The aria-label of the button. Must be provided when using Icon only button.
   * @type {string}
   */
  export let ariaLabel = undefined;

  /**
   * The text to be displayed when the button is in the submitting state.
   * @type {string}
   */
  export let submittingText = "Submitting, please wait.";

  /**
   * The name of the icon to be displayed in the button.
   * It should match one of the keys in the ICON_NAMES object.
   * @type {keyof typeof ICON_NAMES}
   */
  export let iconName = undefined;

  export let isLoading = false;

  /**
   * The alignment of the icon in the button.
   * It should match one of the values in the VALID_ICON_ALIGNMENTS array.
   * @type {string}
   */
  export let iconAlignment = "leading";
  validatePropValue({
    propName: "iconAlignment",
    propValue: iconAlignment,
    validPropValues: VALID_ICON_ALIGNMENTS,
    componentName: "Button"
  });

  /** @type {import('../utilities/themes').Theme['componentColorTheme']}*/
  $: componentColorTheme =
    componentColorThemeOverride || (getTheme(theme)?.componentColorTheme ?? "white");

  /**
   * @type {import('../utilities/themes.js').ThemeTable}
   */
  const buttonThemeClasses = {
    white: {
      outline:
        "bg-transparent border-black text-black hover:text-white focus-visible:outline-black",
      filled: "bg-black text-white hover:text-black border-black focus-visible:outline-black"
    },
    black: {
      outline:
        "bg-transparent text-white hover:text-black border-white focus-visible:outline-white",
      filled: "bg-white text-black hover:text-white border-white focus-visible:outline-white"
    },
    cobaltBlue: {
      outline:
        "bg-transparent border-cobalt-blue text-cobalt-blue hover:text-white focus-visible:outline-cobalt-blue",
      filled:
        "bg-cobalt-blue text-white hover:text-cobalt-blue border-cobalt-blue focus-visible:outline-cobalt-blue"
    }
  };

  const hoverThemeClasses = {
    white: {
      outline: "hover:bg-black",
      filled: "hover:bg-transparent"
    },
    black: {
      outline: "hover:bg-white",
      filled: "hover:bg-transparent"
    },
    cobaltBlue: {
      outline: "hover:bg-cobalt-blue",
      filled: "hover:bg-transparent"
    }
  };

  const disabledThemeClasses = "disabled:bg-transparent disabled:border-grey disabled:text-grey";

  const hasValidLabel = label?.length > 0;
  const hasIcon = $$slots.icon || iconName;
  const spacingClasses =
    hasIcon && !hasValidLabel ? "p-4" : "px-5 py-4 lg:px-6 lg:py-4 xl:px-8 xl:py-5";
  const iconSpacingClasses = iconAlignment === "leading" ? "mr-2 xl:mr-4" : "ml-2 xl:ml-4";
  const iconAlignmentClasses = iconAlignment === "leading" ? "" : "order-1";
  $: themeClasses = buttonThemeClasses[componentColorTheme][variant];
  $: hoverClasses = hoverThemeClasses[componentColorTheme][variant];
</script>

<svelte:element
  this={url ? "a" : "button"}
  bind:this={ref}
  {id}
  data-sveltekit-reload={url ? true : undefined}
  aria-label={ariaLabel}
  type={url ? undefined : type}
  href={url ? url : undefined}
  role={url ? undefined : "button"}
  class="cta-md relative border-thin {themeClasses} {disabled
    ? disabledThemeClasses
    : ''} {!disabled && !isLoading
    ? hoverClasses
    : ''} {spacingClasses} flex items-center transition-colors focus-visible:outline-dashed focus-visible:outline-offset-2 {buttonClass ||
    'relative'}"
  disabled={disabled || isLoading}
  on:focus
  on:click
  {...$$restProps}
>
  {#if isLoading}
    <LoadingIndicator
      class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
      {theme}
      accessibilityLabel={submittingText}
    />
  {/if}
  {#if $$slots.icon || Object.keys(ICON_NAMES).some((name) => name === iconName)}
    <span
      class="{hasValidLabel && iconSpacingClasses} {iconAlignmentClasses} {isLoading
        ? 'invisible'
        : ''}"
    >
      <slot name="icon">
        <Icon name={iconName} />
      </slot>
    </span>
  {/if}
  {#if hasValidLabel}
    <span class={isLoading ? "invisible" : ""}>{label}</span>
  {/if}
  <slot />
</svelte:element>
